import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';

export interface AlertOptions {
  type: AlertTypeEnum;
  message: string;
  title?: string;
  timeout?: number;
  actionText?: string;
  onAction?: () => void;
  showConfirmButton?: boolean;
  position?: 'top' | 'top-start' | 'top-end' | 'center' | 'center-start' | 'center-end' | 'bottom' | 'bottom-start' | 'bottom-end';
}

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  constructor() {}

  showAlert(type: AlertTypeEnum, title: string) {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: type,
      title,
      showConfirmButton: false,
      timerProgressBar: true,
      timer: 1500,
    });
  }

  /**
   * Enhanced alert method with more options
   */
  create(options: AlertOptions) {
    const config: any = {
      toast: !options.showConfirmButton,
      position: options.position || 'top-end',
      icon: options.type,
      title: options.title || options.message,
      text: options.title ? options.message : undefined,
      showConfirmButton: options.showConfirmButton || !!options.actionText,
      timerProgressBar: options.timeout !== 0,
      timer: options.timeout === 0 ? undefined : (options.timeout || 3000),
    };

    if (options.actionText) {
      config.confirmButtonText = options.actionText;
      config.confirmButtonColor = '#3085d6';
    }

    if (options.timeout === 0) {
      config.toast = false;
      config.position = 'center';
    }

    const swalPromise = Swal.fire(config);

    if (options.onAction) {
      swalPromise.then((result) => {
        if (result.isConfirmed && options.onAction) {
          options.onAction();
        }
      });
    }

    return swalPromise;
  }

  /**
   * Show confirmation dialog
   */
  confirm(options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
  }): Promise<boolean> {
    return Swal.fire({
      title: options.title,
      text: options.message,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: options.confirmText || 'Yes',
      cancelButtonText: options.cancelText || 'Cancel'
    }).then((result) => result.isConfirmed);
  }

  /**
   * Prompt for a new password (and confirmation) using a modal.
   * Returns null if cancelled.
   */
  async promptNewPassword(options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    minLength?: number;
  }): Promise<{ password: string; passwordConfirmation: string } | null> {
    const minLength = typeof options.minLength === 'number' ? options.minLength : 8;

    const result = await Swal.fire({
      title: options.title,
      html: `
        <div style="text-align:left; margin-top: 0.5rem;">
          <div style="margin-bottom: 0.75rem;">${options.message}</div>
          <input id="swal-new-password" type="password" class="swal2-input" placeholder="New password" autocomplete="new-password" />
          <input id="swal-confirm-password" type="password" class="swal2-input" placeholder="Confirm password" autocomplete="new-password" />
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: options.confirmText || 'Set Password',
      cancelButtonText: options.cancelText || 'Cancel',
      confirmButtonColor: '#3085d6',
      focusConfirm: false,
      didOpen: () => {
        const el = document.getElementById('swal-new-password') as HTMLInputElement | null;
        el?.focus();
      },
      preConfirm: () => {
        const passwordEl = document.getElementById('swal-new-password') as HTMLInputElement | null;
        const confirmEl = document.getElementById('swal-confirm-password') as HTMLInputElement | null;

        const password = String(passwordEl?.value ?? '');
        const passwordConfirmation = String(confirmEl?.value ?? '');

        if (!password || !passwordConfirmation) {
          Swal.showValidationMessage('Please enter and confirm the new password.');
          return;
        }

        if (password.length < minLength) {
          Swal.showValidationMessage(`Password must be at least ${minLength} characters.`);
          return;
        }

        if (password !== passwordConfirmation) {
          Swal.showValidationMessage('Passwords do not match.');
          return;
        }

        return { password, passwordConfirmation };
      },
    });

    if (!result.isConfirmed || !result.value) return null;
    return result.value;
  }

  /**
   * Prompt for a required reason string.
   * Returns null if cancelled.
   */
  async promptReason(options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    placeholder?: string;
  }): Promise<string | null> {
    const result = await Swal.fire({
      title: options.title,
      html: `
        <div style="text-align:left; margin-top: 0.5rem;">
          <div style="margin-bottom: 0.75rem; white-space: pre-line;">${options.message}</div>
          <input id="swal-reason" type="text" class="swal2-input" placeholder="${options.placeholder ?? 'Enter reason'}" />
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: options.confirmText || 'Confirm',
      cancelButtonText: options.cancelText || 'Cancel',
      confirmButtonColor: '#3085d6',
      focusConfirm: false,
      didOpen: () => {
        const el = document.getElementById('swal-reason') as HTMLInputElement | null;
        el?.focus();
      },
      preConfirm: () => {
        const el = document.getElementById('swal-reason') as HTMLInputElement | null;
        const reason = String(el?.value ?? '').trim();
        if (!reason) {
          Swal.showValidationMessage('Reason is required.');
          return;
        }
        return reason;
      },
    });

    if (!result.isConfirmed || !result.value) return null;
    return String(result.value);
  }

  /**
   * Prompt for defaulter payment inputs.
   * Returns null if cancelled.
   */
  async promptDefaulterPayment(options: {
    title: string;
    messageHtml: string;
    totalPenalty: number;
    confirmText?: string;
    cancelText?: string;
  }): Promise<{ paidAmount: number; discountAmount: number; discountReason: string | null } | null> {
    const totalPenalty = Number(options.totalPenalty || 0);

    const result = await Swal.fire({
      title: options.title,
      html: `
        <div style="text-align:left; margin-top: 0.5rem;">
          <div style="margin-bottom: 0.75rem;">${options.messageHtml}</div>
          <input id="swal-paid-amount" type="number" class="swal2-input" placeholder="Actual amount paid" min="0" step="1" />
          <input id="swal-discount-amount" type="number" class="swal2-input" placeholder="Discount / forgiven amount (optional)" min="0" step="1" />
          <input id="swal-discount-reason" type="text" class="swal2-input" placeholder="Discount reason (required if discount entered)" />
          <div style="font-size: 0.85rem; margin-top: 0.5rem; color: #6c757d;">
            Total penalty to settle: <strong>${new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', minimumFractionDigits: 0 }).format(totalPenalty)}</strong>
          </div>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: options.confirmText || 'Record Payment',
      cancelButtonText: options.cancelText || 'Cancel',
      confirmButtonColor: '#3085d6',
      focusConfirm: false,
      didOpen: () => {
        const el = document.getElementById('swal-paid-amount') as HTMLInputElement | null;
        el?.focus();
      },
      preConfirm: () => {
        const paidEl = document.getElementById('swal-paid-amount') as HTMLInputElement | null;
        const discountEl = document.getElementById('swal-discount-amount') as HTMLInputElement | null;
        const reasonEl = document.getElementById('swal-discount-reason') as HTMLInputElement | null;

        const paidAmount = Number(paidEl?.value ?? '');
        const discountAmount = Number(discountEl?.value ?? 0);
        const discountReason = String(reasonEl?.value ?? '').trim();

        if (!Number.isFinite(paidAmount) || paidAmount < 0) {
          Swal.showValidationMessage('Please enter a valid paid amount.');
          return;
        }

        if (!Number.isFinite(discountAmount) || discountAmount < 0) {
          Swal.showValidationMessage('Please enter a valid discount amount.');
          return;
        }

        if (discountAmount > 0 && !discountReason) {
          Swal.showValidationMessage('Discount reason is required when discount amount is provided.');
          return;
        }

        const sum = paidAmount + discountAmount;
        if (Math.abs(sum - totalPenalty) > 0.01) {
          Swal.showValidationMessage('Paid amount plus discount must equal the total penalty.');
          return;
        }

        return { paidAmount, discountAmount, discountReason: discountAmount > 0 ? discountReason : null };
      },
    });

    if (!result.isConfirmed || !result.value) return null;
    return result.value;
  }
}

export enum AlertTypeEnum {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  DANGER = 'error', // Alias for ERROR
}
